import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  model,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrDatagrid, ClrForm, ClrLoadingState } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ArrayMethodsCallbackPipe } from 'src/app/pipes/array-methods-callback.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { LinieDrehungPipe } from 'src/app/pipes/linie-drehung.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { LinieDrehungService } from 'src/app/services/Shared/linie-drehung.service';
import { PreviewThumbnailsComponent } from '../../_shared/preview-thumbnails/preview-thumbnails.component';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { Table } from 'src/app/models/ui/table';
import { CachedMediaStore } from 'src/app/shared/stores/media-cached.store';
import { MediaApiFunc } from 'src/app/services/MediaApiFunc/abstract-media-api-func';
import { AnlagenMediaApiFuncService, MEDIA_TITLE } from 'src/app/services/MediaApiFunc/anlagen-media-api-func.service';
import { FileObject } from 'src/app/services/base64/base64.service';
import { EntsperrAnlageService } from 'src/app/services/Shared/entsperr-anlage.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { DatalistInputComponent } from '../../_shared/datalist-input/datalist-input.component';
import { calcPosition, GeoAutofill } from 'src/app/services/Shared/geo-autofill';
import { calcDirection } from 'src/app/shared/classes/linie-validation-manager';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-ereignisdialog',
  templateUrl: './ereignisdialog.component.html',
  styleUrl: './ereignisdialog.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    PreviewThumbnailsComponent,
    DatalistInputComponent,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
    LinieDrehungPipe,
    ArrayMethodsCallbackPipe,
  ],
  providers: [
    LinieDrehungPipe,
    { provide: MEDIA_TITLE, useValue: 'Ereignisse' },
    { provide: MediaApiFunc, useClass: AnlagenMediaApiFuncService },
    CachedMediaStore,
    GeoAutofill,
  ],
})
export class EreignisdialogComponent {
  private readonly apiService = inject(APIService);
  private readonly toastr = inject(ToastService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly cachedMedia = inject(CachedMediaStore);
  private readonly confirmService = inject(ConfirmService);
  private readonly geoAutofill = inject(GeoAutofill);
  protected readonly drehungPipe = inject(LinieDrehungPipe);
  protected readonly drehung = inject(LinieDrehungService);

  public readonly closedialog = new BehaviorSubject(false);
  private dataRefresh: EventEmitter<any>;

  private readonly preview = viewChild(PreviewThumbnailsComponent);
  private readonly grid = viewChild(ClrDatagrid);
  private readonly clrForm = viewChild(ClrForm);

  readonly bezeichnung = model<string>(undefined, { alias: 'setbezeichnung' });
  readonly otypid = model<number>(undefined, { alias: 'setotypid' });
  readonly ostammid = model<number>(undefined, { alias: 'setostammid' });
  readonly stammdata = model<{ startmeter: number; endmeter: number }>(
    { startmeter: 0, endmeter: 0 },
    { alias: 'setstammData' }
  );

  private readonly startmeter = computed(() => this.stammdata().startmeter);
  private readonly endmeter = computed(() => this.stammdata().endmeter);
  protected readonly direction = computed(() => calcDirection(this.startmeter(), this.endmeter()));
  protected readonly selectedNatur = signal<EreignisRow>(undefined);

  protected exceptStartEnd = (col: any) => !['position'].includes(col.id);
  protected readonly table = signal<Table<EreignisRow>>({ columns: [], rows: [] });
  protected readonly typListe = signal<string[]>([]);
  protected readonly beschreibungListe = signal<string[]>([]);
  protected readonly isModeNew = signal(false);

  protected readonly isInspektion = signal(true);
  protected readonly deletedFiles = signal<string[]>([]);
  protected readonly saveState = signal(ClrLoadingState.DEFAULT);
  protected readonly geoState = signal(ClrLoadingState.DEFAULT);

  protected readonly minMeter = computed(() => Math.min(this.startmeter(), this.endmeter()));
  private readonly maxMeter = computed(() => Math.max(this.startmeter(), this.endmeter()));

  public set data(dataIn: any) {
    this.isInspektion.set(false);
    if (dataIn.table) this.table.set(dataIn.table);
    this.ostammid.set(dataIn.ostamm);
    this.otypid.set(dataIn.otypid);
    this.bezeichnung.set(dataIn.name);
    this.stammdata.set(dataIn.stammData);
    this.dataRefresh = dataIn.dataRefresh;
  }

  protected formEreignisTop = new FormGroup({
    position: new FormControl<number>(undefined, { updateOn: 'blur' }),
    typ: new FormControl<string>('', [Validators.required]),
    beschreibung: new FormControl<string>(''),
    bemerkung: new FormControl<string>(''),
  });

  constructor() {
    inject(EntsperrAnlageService).registerDestroyRef(inject(DestroyRef));

    this.geoAutofill.registerOSTAMMID(this.ostammid);

    effect(() => {
      if (this.ostammid() && (this.isInspektion() || !this.table().columns.length)) untracked(() => this.freshReload());
    });

    effect(() => {
      const selected = this.selectedNatur();
      untracked(() => {
        this.formEreignisTop.reset();
        if (!selected) return;
        this.isModeNew.set(false);
        this.formEreignisTop.enable();
        this.formEreignisTop.patchValue(selected);
        this.clrForm().markAsTouched();

        this.cachedMedia.getBilder(selected.id, selected.bild, this.preview());
      });
    });

    effect(() => {
      const min = this.minMeter();
      const max = this.maxMeter();

      const posCtrl = this.formEreignisTop.get('position');
      posCtrl.setValidators([Validators.required, Validators.min(min), Validators.max(max)]);
      posCtrl.updateValueAndValidity();
    });
  }

  async ngOnInit() {
    this.formEreignisTop.disable();
    const arten = await firstValueFrom(this.apiService.getEreignisTypen(this.otypid()));
    this.typListe.set(arten['typ']);
    this.beschreibungListe.set(arten['beschreibung']);
  }

  protected addEreignis() {
    this.isModeNew.set(true);
    this.grid()?.selection.clearSelection();
    this.formEreignisTop.enable();

    setTimeout(() => {
      this.preview().clearPreview();
    });
  }

  protected async deleteTitelEreignis() {
    const text = this.mrTranslate.transform('Eintrag wirklich löschen?');
    const ok = await this.confirmService.danger(text);
    if (ok && this.selectedNatur()) {
      const id = this.selectedNatur().id;
      const { success, error } = await firstValueFrom(this.apiService.deleteEreignis(id));
      if (success) {
        this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
        this.freshReload();
        this.cachedMedia.deleteInCache(id, this.preview());
        this.formEreignisTop.disable();
        this.grid()?.selection.clearSelection();
        this.dataRefresh?.emit(true);
      } else {
        console.log(error);
        this.toastr.error(this.mrTranslate.transform('Fehler beim Löschen'));
      }
    }
  }

  private async freshReload() {
    const bawerkeTable = await firstValueFrom(this.apiService.getEreignis(this.ostammid()));
    this.table.update(table => {
      if (!table?.columns?.length) table = bawerkeTable;
      else table.rows = bawerkeTable.rows;
      return { ...table };
    });
  }

  protected saveIfValid() {
    this.clrForm().markAsTouched();
    if (this.formEreignisTop.valid) this.saveEreignis();
  }

  private async saveEreignis() {
    this.saveState.set(ClrLoadingState.LOADING);

    const sendObj: EreignisModel = {
      ID: this.selectedNatur()?.id ?? -1,
      table: this.formEreignisTop.value,
      mediaChange: {
        files: await this.preview().packUnsavedFiles(),
        savedFiles: this.preview().savedNames() ?? '',
        deletedFiles: this.deletedFiles(),
      },
    };

    const { success, error, id } = await firstValueFrom(this.apiService.setEreignis(this.ostammid(), sendObj));

    if (success) {
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
      this.cachedMedia.reloadAfterSaved(id, this.preview(), this.deletedFiles());
      this.deletedFiles.set([]);
      this.freshReload();
      this.isModeNew.set(false);
      this.grid().selected = [{ id }];
      this.dataRefresh?.emit(true);
      this.saveState.set(ClrLoadingState.SUCCESS);
    } else {
      console.log(error);
      this.toastr.error(this.mrTranslate.transform('Fehler beim Speichern'));
      this.saveState.set(ClrLoadingState.DEFAULT);
    }
  }

  public checkValid(startmeter: number, endmeter: number) {
    this.stammdata.set({ startmeter, endmeter });

    const valid = this.table().rows.every(row =>
      startmeter < endmeter ? +row.position >= startmeter : +row.position <= startmeter
    );
    if (!valid)
      this.toastr.error(
        this.mrTranslate.transform('Berücksichtigen Sie die Start- & Endmeter'),
        this.mrTranslate.transform('Ereignis')
      );
    return valid;
  }

  protected async findPositionAndAutofill() {
    this.geoState.set(ClrLoadingState.LOADING);
    const snapped = await this.geoAutofill.getSnappedLength();
    this.geoState.set(ClrLoadingState.DEFAULT);
    if (snapped === null) return;

    const position = calcPosition({
      snapped,
      start: this.startmeter(),
      end: this.endmeter(),
      direction: this.direction(),
    });

    this.formEreignisTop.controls.position.setValue(position);
  }

  protected deleteFile(name: string) {
    this.deletedFiles.update(names => (names.push(name), [...names]));
  }

  protected close() {
    this.closedialog.next(true);
  }
}

export interface EreignisRow {
  id: number;
  ostammid: number;
  position: number;
  typ: string;
  beschreibung: string;
  bild: string;
  benutzername: string;
  status: string;
}

export interface EreignisModel {
  ID: number;
  table: Partial<{
    position: number;
    typ: string;
    beschreibung: string;
  }>;
  mediaChange: {
    files: FileObject[];
    savedFiles: string;
    deletedFiles: string[];
  };
}
